// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Check, Info, Loader2 } from 'lucide-react';
import { MOCK } from '../../data/mock';
import { MOCK_TICKET_TIERS_BY_EVENT } from '../../data/sales';
import { confirmOrderDashboard, createOrder, listCustomers, loadEventPages, loadEventTicketTypes, createComplimentaryOrder } from '../../lib/backOfficeApi';
import { logAudit, toast } from '../../lib/buses';
import { Field, Input, Select, Textarea } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { EventSelect } from '../../components/filters/EventSelect';
import { OtpPromptModal } from '../../ui/OtpPromptModal';
import { WizardFooter, WizardSteps } from '../../ui/Wizard';
import { shortId } from '../../lib/format';
import { IdTag } from '../../ui/IdTag';

const TIER_PRICE = (tier) => Number(tier?.price ?? tier?.netPrice ?? tier?.unitPrice ?? 0);
const TIER_NAME = (tier) => tier?.tier || tier?.name || tier?.title || tier?.id;
const TIER_AVAILABLE = (tier) => Number(tier?.available ?? tier?.availableQuantity ?? tier?.inventoryRemaining ?? 0);
const MANUAL_ORDER_STEPS = [
  { id: 'customer', label: 'Customer' },
  { id: 'tickets',  label: 'Tickets' },
  { id: 'payment',  label: 'Payment & deliver' },
];

export const NewManualOrderModal = ({ onClose, onCreate, presetEventId, presetCustomerName }) => {
  const [step, setStep] = useState('customer');
  // Customer
  const [custMode, setCustMode] = useState(presetCustomerName ? 'existing' : 'new');
  const [custQuery, setCustQuery] = useState(presetCustomerName || '');
  const [custSelected, setCustSelected] = useState(null);
  const [newCust, setNewCust] = useState({ name: presetCustomerName || '', email: '', phone: '', country: 'Uganda' });
  // Order
  const [eventId, setEventId] = useState(presetEventId || '');
  const [tickets, setTickets] = useState({});
  const [notesInternal, setNotesInternal] = useState('');
  // Payment
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [discount, setDiscount] = useState({ enabled: false, kind: 'pct', value: 0 });
  const [comp, setComp] = useState(false);
  const [reference, setReference] = useState('');
  const [deliverEmail, setDeliverEmail] = useState(true);
  const [deliverWhatsapp, setDeliverWhatsapp] = useState(true);
  const [deliverSms, setDeliverSms] = useState(false);

  // Backend-driven data
  const [events, setEvents] = useState(() => MOCK.events.filter((e) => e.status === 'Live' || e.status === 'Upcoming'));
  const [eventsLoading, setEventsLoading] = useState(false);
  const [tiers, setTiers] = useState(() => MOCK_TICKET_TIERS_BY_EVENT.default || []);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [tiersError, setTiersError] = useState('');
  // Customer search (real /api/users/customers)
  const [customerMatches, setCustomerMatches] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null); // { orderId, paymentMethod }

  useEffect(() => {
    let cancelled = false;
    setEventsLoading(true);
    // You sell tickets for events happening now or soon — load only live/upcoming
    // (server-bounded by date>=today), not every event ever.
    loadEventPages({ page: 1, limit: 50, includeStats: false, upcoming: true, signal: undefined })
      .then((result) => {
        if (cancelled) return;
        if (result?.rows?.length) setEvents(result.rows);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setEventsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!eventId) { setTiers([]); setTiersError(''); return undefined; }
    let cancelled = false;
    setTiersLoading(true);
    setTiersError('');
    loadEventTicketTypes(eventId)
      .then((result) => {
        if (cancelled) return;
        const rows = Array.isArray(result) ? result : (result?.tickets || []);
        const mapped = rows.map((tier) => ({
          id: tier.id,
          name: TIER_NAME(tier),
          price: TIER_PRICE(tier),
          available: TIER_AVAILABLE(tier),
          currency: tier.currency || null,
        })).filter((tier) => tier.id);
        if (mapped.length) {
          setTiers(mapped);
        } else {
          setTiers(MOCK_TICKET_TIERS_BY_EVENT.default || []);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setTiers(MOCK_TICKET_TIERS_BY_EVENT.default || []);
        setTiersError(err?.message || 'Using offline pricing');
      })
      .finally(() => { if (!cancelled) setTiersLoading(false); });
    return () => { cancelled = true; };
  }, [eventId]);

  const eligibleEvents = useMemo(() => events, [events]);
  const selectedEvent = eligibleEvents.find((e) => e.id === eventId);
  const totalQty = Object.values(tickets).reduce((s, q) => s + (q || 0), 0);
  const subtotal = Object.entries(tickets).reduce((s, [tid, q]) => {
    const t = tiers.find((x) => x.id === tid);
    return s + (t ? TIER_PRICE(t) * (q || 0) : 0);
  }, 0);
  const discountAmt = comp ? subtotal : (discount.enabled ? (discount.kind === 'pct' ? Math.round(subtotal * discount.value / 100) : discount.value) : 0);
  const total = Math.max(0, subtotal - discountAmt);

  const customerName = custMode === 'existing' ? (custSelected?.name || '') : custMode === 'walkin' ? 'Walk-in customer' : newCust.name;
  const customerContact = custMode === 'existing' ? (custSelected?.email || custSelected?.phone || '') : custMode === 'walkin' ? '' : (newCust.email || newCust.phone);

  // Customer search via /api/users/customers — debounced.
  useEffect(() => {
    if (custMode !== 'existing') { setCustomerMatches([]); return undefined; }
    let cancelled = false;
    setCustomerSearching(true);
    const handle = setTimeout(() => {
      listCustomers({ limit: 10, search: custQuery || undefined, signal: undefined })
        .then((result) => {
          if (cancelled) return;
          setCustomerMatches(result.customers.map((c) => ({
            id: c.id,
            name: c.name,
            email: c.email,
            phone: c.phone,
          })));
        })
        .catch(() => {
          if (cancelled) return;
          setCustomerMatches([]);
        })
        .finally(() => { if (!cancelled) setCustomerSearching(false); });
    }, 220);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [custQuery, custMode]);

  const canContinueCustomer = (custMode === 'existing' && custSelected) || (custMode === 'walkin') || (custMode === 'new' && newCust.name.trim() && (newCust.email || newCust.phone));
  const canContinueTickets = eventId && totalQty > 0;
  const canSubmit = canContinueCustomer && canContinueTickets && (comp || total === 0 || paymentMethod);

  const STEPS = MANUAL_ORDER_STEPS;

  const incQty = (tierId) => setTickets((t) => ({ ...t, [tierId]: (t[tierId] || 0) + 1 }));
  const decQty = (tierId) => setTickets((t) => ({ ...t, [tierId]: Math.max(0, (t[tierId] || 0) - 1) }));
  const setQty = (tierId, n) => setTickets((t) => ({ ...t, [tierId]: Math.max(0, parseInt(n, 10) || 0) }));

  const buildPayload = () => {
    const [firstName, ...rest] = (newCust.name || customerName || 'Customer').trim().split(/\s+/);
    const selectedTickets = Object.entries(tickets)
      .filter(([, q]) => q > 0)
      .map(([id, count]) => ({ id, count }));
    return {
      eventId,
      paymentMethod: comp ? 'cash' : paymentMethod,
      orderType: comp ? 'complimentary' : 'paid',
      firstName: firstName || 'Customer',
      lastName: rest.join(' '),
      email: newCust.email || custSelected?.email || undefined,
      phone: newCust.phone || custSelected?.phone || undefined,
      isBuyerRecipient: true,
      selectedTickets,
    };
  };

  const submit = async () => {
    if (submitting || pendingConfirm) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();

      // A COMP IS NOT A PAID ORDER WITH A FLAG ON IT.
      //
      // This used to POST /api/orders with `orderType: 'complimentary'` — but that handler
      // ignores orderType entirely (orderCreateRoutes: the field is in the schema, nothing reads
      // it), so what got created was a plain PENDING PAID order, which an OTP-gated confirm then
      // settled by a different mechanism. None of the comp semantics existed on it: no `comp`
      // settlement rail, no zero-money settle, no issuance reason in misc. The event page's comp
      // form has always used /api/orders/complimentary, which does all three and delivers the
      // tickets — which is exactly why comps worked there and not here.
      //
      // One act, one path. Paid manual orders keep their own flow below: recording money taken
      // outside a gateway is a different act, and the OTP belongs to that, not to this.
      if (comp) {
        const created = await createComplimentaryOrder({
          eventId,
          selectedTickets: payload.selectedTickets,
          firstName: payload.firstName,
          lastName: payload.lastName,
          email: payload.email,
          phone: payload.phone,
          reason: 'Manual order (box office)',
        });
        const compId = created?.id || created?.order?.id;
        logAudit({ action: 'Issued complimentary order', detail: `${payload.firstName} ${payload.lastName} · ${totalQty} tickets · ${payload.eventId}`, target: compId || eventId });
        if (onCreate) onCreate({ id: compId, customer: customerName, event: eventId, total: 0, comp: true, qty: totalQty });
        toast('Complimentary tickets issued', 'success', `${customerName} · ${totalQty} ticket${totalQty !== 1 ? 's' : ''} · tickets delivered`);
        onClose && onClose();
        return;
      }

      const order = await createOrder(payload);
      const orderId = order?.id;
      if (!orderId) throw new Error('Backend did not return an order id');
      logAudit({ action: 'Created manual order', detail: `${payload.firstName} ${payload.lastName} · ${totalQty} tickets · ${payload.eventId}`, target: orderId });
      setPendingConfirm({
        orderId,
        paymentMethod: comp ? 'cash' : paymentMethod,
        summary: `${customerName} · ${totalQty} ticket${totalQty !== 1 ? 's' : ''} · ${comp ? 'Comp' : paymentMethod}`,
      });
    } catch (err) {
      toast('Order creation failed', 'error', err?.message || 'Backend rejected the payload');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpVerified = async (otp) => {
    if (!pendingConfirm) return;
    try {
      await confirmOrderDashboard({
        orderId: pendingConfirm.orderId,
        paymentMethod: pendingConfirm.paymentMethod,
        otp,
        reason: notesInternal || undefined,
      });
      logAudit({ action: 'Confirmed manual order', detail: `${pendingConfirm.summary} · OTP verified`, target: pendingConfirm.orderId });
      if (onCreate) onCreate({ id: pendingConfirm.orderId, customer: customerName, event: eventId, total, comp, qty: totalQty });
      toast('Manual order confirmed', 'success', `${pendingConfirm.orderId} · ${pendingConfirm.summary} · tickets delivered`);
      setPendingConfirm(null);
      onClose();
    } catch (err) {
      toast('Authorization failed', 'error', err?.message || 'Could not confirm the order');
      setPendingConfirm(null);
    }
  };


  return (
    <Modal open={true} onClose={onClose} width="max-w-[760px]" title="New manual order" sub="Box-office, agent-assisted, or admin override · creates a paid or comp order outside the customer-facing flow">
      <WizardSteps steps={STEPS} current={step} />

      {step === 'customer' && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'existing', label: 'Existing customer', sub: 'Look up by name, email, phone' },
              { id: 'new',      label: 'New customer',      sub: 'Capture contact details now' },
              { id: 'walkin',   label: 'Walk-in (anon)',    sub: 'No contact captured · paper tickets only' },
            ].map(o => (
              <button key={o.id} onClick={() => { setCustMode(o.id); setCustSelected(null); }} className={`text-left rounded-md border px-3 py-2.5 ${custMode === o.id ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] bg-white hover:bg-[#FBFAF7]'}`}>
                <div className="text-[12.5px] font-semibold text-[#1F2937]">{o.label}</div>
                <div className="text-[10.5px] text-[#6B7280] mt-0.5 leading-snug">{o.sub}</div>
              </button>
            ))}
          </div>

          {custMode === 'existing' && (
            <div className="space-y-2">
              <Field label="Search customer" sub="Searches /api/users/customers by name, email, or phone">
                <div className="relative">
                  <Input value={custQuery} onChange={e => { setCustQuery(e.target.value); setCustSelected(null); }} placeholder="Type to search…" />
                  {customerSearching && <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />}
                </div>
              </Field>
              <div className="border border-[#E6E4E1] rounded-md overflow-hidden">
                {customerMatches.length === 0 && !customerSearching && <div className="px-3 py-3 text-[12px] text-[#9CA3AF]">No matches in the customer database</div>}
                {customerMatches.length === 0 && customerSearching && <div className="px-3 py-3 text-[12px] text-[#9CA3AF] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Searching…</div>}
                {customerMatches.map(c => {
                  const sel = custSelected?.id === c.id;
                  return (
                    <button key={c.id} onClick={() => setCustSelected(c)} className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-[#EEEDEA] last:border-b-0 ${sel ? 'bg-[#FBFAF7]' : 'bg-white hover:bg-[#FBFAF7]'}`}>
                      <div className={`w-4 h-4 rounded-full border ${sel ? 'border-[#1F2937] bg-[#1F2937]' : 'border-[#D6D3D1]'} flex items-center justify-center flex-shrink-0`}>{sel && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-medium text-[#1F2937]">{c.name}</div>
                        <div className="text-[10.5px] text-[#6B7280] mt-0.5">{c.email || '—'} · {c.phone || '—'}</div>
                      </div>
                      <div className="text-[10.5px] text-[#9CA3AF] font-mono"><IdTag value={c.id} copyable={false} /></div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {custMode === 'new' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" required><Input value={newCust.name} onChange={e => setNewCust({ ...newCust, name: e.target.value })} placeholder="As it should appear on the ticket" /></Field>
              <Field label="Country"><Select value={newCust.country} onChange={e => setNewCust({ ...newCust, country: e.target.value })} options={['Uganda','Kenya','Tanzania','Rwanda','Other']} /></Field>
              <Field label="Email"><Input type="email" value={newCust.email} onChange={e => setNewCust({ ...newCust, email: e.target.value })} placeholder="name@example.com" /></Field>
              <Field label="Phone"><Input value={newCust.phone} onChange={e => setNewCust({ ...newCust, phone: e.target.value })} placeholder="+256 700 000 000" /></Field>
              <div className="col-span-2 text-[11px] text-[#6B7280]">Email or phone is required to deliver tickets digitally.</div>
            </div>
          )}

          {custMode === 'walkin' && (
            <div className="bg-[#FFF7E6] border border-[#FAE2A8] rounded-md px-3 py-2.5 text-[11.5px] text-[#8A5A12] flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>No contact details will be captured. Tickets are issued as a printable PDF only — there's no way to re-issue these tickets if lost.</div>
            </div>
          )}
        </div>
      )}

      {step === 'tickets' && (
        <div className="space-y-3">
          <Field label="Event" required>
            <EventSelect events={eligibleEvents} value={eventId} onChange={(id) => { setEventId(id); setTickets({}); }} placeholder="Choose event…" className="w-full" onSearch={(term) => loadEventPages({ searchQuery: term, limit: 30, includeStats: false }).then((r) => r.rows).catch(() => [])} />
          </Field>
          {selectedEvent && (
            <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2 text-[11px] text-[#6B7280] flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /><span className="text-[#1F2937] font-medium">{selectedEvent.name}</span> · {selectedEvent.venue} · {selectedEvent.date}
            </div>
          )}

          {eventId && (
            <div>
              <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Choose tickets</div>
              {tiersLoading ? (
                <div className="border border-[#E6E4E1] rounded-md px-3 py-4 flex items-center gap-2 text-[12px] text-[#6B7280]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading ticket tiers from backend…
                </div>
              ) : tiers.length === 0 ? (
                <div className="border border-[#E6E4E1] rounded-md px-3 py-4 text-[12px] text-[#6B7280]">
                  No ticket tiers configured for this event yet.
                </div>
              ) : (
                <div className="border border-[#E6E4E1] rounded-md divide-y divide-[#EEEDEA] overflow-hidden">
                  {tiers.map(t => {
                    const q = tickets[t.id] || 0;
                    const price = TIER_PRICE(t);
                    return (
                      <div key={t.id} className="px-3 py-2.5 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-medium text-[#1F2937]">{t.name}</div>
                          <div className="text-[10.5px] text-[#6B7280] mt-0.5">
                            UGX {price.toLocaleString()}
                            {Number.isFinite(TIER_AVAILABLE(t)) && TIER_AVAILABLE(t) > 0 ? ` · ${TIER_AVAILABLE(t).toLocaleString()} available` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => decQty(t.id)} className="w-6 h-6 rounded border border-[#E6E4E1] text-[#1F2937] hover:bg-[#FBFAF7] inline-flex items-center justify-center text-[14px] leading-none">−</button>
                          <input value={q} onChange={e => setQty(t.id, e.target.value)} className="w-12 h-7 px-2 text-center text-[12.5px] tabular-nums border border-[#E6E4E1] rounded focus:outline-none focus:border-[#1F2937]" />
                          <button onClick={() => incQty(t.id)} className="w-6 h-6 rounded border border-[#E6E4E1] text-[#1F2937] hover:bg-[#FBFAF7] inline-flex items-center justify-center text-[14px] leading-none">+</button>
                        </div>
                        <div className="w-24 text-right text-[12px] text-[#1F2937] tabular-nums font-medium">{q > 0 ? 'UGX ' + (price * q).toLocaleString() : <span className="text-[#D6D3D1]">—</span>}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {tiersError && <div className="text-[11px] text-[#8A5A12] mt-1.5">{tiersError}</div>}
              {totalQty > 0 && (
                <div className="mt-2 flex items-center justify-between bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <span className="text-[11.5px] text-[#6B7280]">{totalQty} ticket{totalQty !== 1 ? 's' : ''} selected</span>
                  <span className="text-[13px] font-semibold text-[#1F2937] tabular-nums">UGX {subtotal.toLocaleString()}</span>
                </div>
              )}
            </div>
          )}

          <Field label="Internal notes" optional sub="Visible to operators on the order timeline · not sent to the customer">
            <Textarea rows={2} value={notesInternal} onChange={e => setNotesInternal(e.target.value)} placeholder="e.g. Cash paid at the gate · supervisor approved by Brian O." />
          </Field>
        </div>
      )}

      {step === 'payment' && (
        <div className="space-y-3">
          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-3 space-y-1.5">
            <div className="flex items-center justify-between text-[11.5px] text-[#6B7280]"><span>Customer</span><span className="text-[#1F2937] font-medium">{customerName}{customerContact ? ` · ${customerContact}` : ''}</span></div>
            <div className="flex items-center justify-between text-[11.5px] text-[#6B7280]"><span>Event</span><span className="font-mono text-[#1F2937]">{eventId}</span></div>
            <div className="flex items-center justify-between text-[11.5px] text-[#6B7280]"><span>Tickets</span><span className="text-[#1F2937] font-medium">{totalQty}</span></div>
            <div className="flex items-center justify-between text-[11.5px] pt-1 border-t border-[#EEEDEA]"><span className="text-[#6B7280]">Subtotal</span><span className="text-[#1F2937] tabular-nums">UGX {subtotal.toLocaleString()}</span></div>
            {discountAmt > 0 && <div className="flex items-center justify-between text-[11.5px]"><span className="text-[#6B7280]">{comp ? 'Comp (100% off)' : 'Discount'}</span><span className="text-[#9B1F1F] tabular-nums">− UGX {discountAmt.toLocaleString()}</span></div>}
            <div className="flex items-center justify-between pt-1 border-t border-[#EEEDEA]"><span className="text-[12px] font-semibold text-[#1F2937]">Total</span><span className="text-[14px] font-bold text-[#1F2937] tabular-nums">{comp ? 'COMP' : `UGX ${total.toLocaleString()}`}</span></div>
          </div>

          {!comp && (
            <Field label="Payment method" required>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'cash',     label: 'Cash',                     sub: 'Box-office or runner' },
                  { id: 'momo',     label: 'Mobile Money (MoMo)',      sub: 'MTN / Airtel' },
                  { id: 'card',     label: 'Card',                     sub: 'POS terminal · Visa / Mastercard' },
                  { id: 'bank',     label: 'Bank EFT',                 sub: 'Off-platform · reference required' },
                  { id: 'wallet',   label: 'TKT Wallet',               sub: 'Internal balance' },
                  { id: 'voucher',  label: 'Voucher / promo code',     sub: 'Apply a redemption code' },
                ].map(p => (
                  <label key={p.id} className={`flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer ${paymentMethod === p.id ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                    <input type="radio" name="pay" checked={paymentMethod === p.id} onChange={() => setPaymentMethod(p.id)} className="mt-0.5" />
                    <div className="min-w-0"><div className="text-[12px] font-medium text-[#1F2937]">{p.label}</div><div className="text-[10.5px] text-[#6B7280] mt-0.5">{p.sub}</div></div>
                  </label>
                ))}
              </div>
            </Field>
          )}

          {!comp && (paymentMethod === 'bank' || paymentMethod === 'voucher') && (
            <Field label={paymentMethod === 'bank' ? 'Bank reference' : 'Voucher code'} required>
              <Input value={reference} onChange={e => setReference(e.target.value)} placeholder={paymentMethod === 'bank' ? 'Bank txn ref · e.g. STAN-9982712' : 'e.g. PRESS2026'} />
            </Field>
          )}

          <div className="border-t border-[#EEEDEA] pt-3 space-y-2">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1">Adjustments</div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={comp} onChange={e => { setComp(e.target.checked); if (e.target.checked) setDiscount({ enabled: false, kind: 'pct', value: 0 }); }} className="mt-0.5" />
              <div><div className="text-[12px] font-medium text-[#1F2937]">Issue as complimentary</div><div className="text-[10.5px] text-[#6B7280]">100% off · counts against the comp allocation · supervisor sign-off required for ≥10 tickets.</div></div>
            </label>
            {!comp && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={discount.enabled} onChange={e => setDiscount({ ...discount, enabled: e.target.checked })} className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-[12px] font-medium text-[#1F2937]">Apply manual discount</div>
                  <div className="text-[10.5px] text-[#6B7280]">Requires reason; logged to the audit trail.</div>
                  {discount.enabled && (
                    <div className="flex items-center gap-2 mt-2">
                      <select value={discount.kind} onChange={e => setDiscount({ ...discount, kind: e.target.value })} className="h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded bg-white">
                        <option value="pct">Percentage off</option>
                        <option value="amt">Flat amount (UGX)</option>
                      </select>
                      <input type="number" value={discount.value} onChange={e => setDiscount({ ...discount, value: parseInt(e.target.value, 10) || 0 })} className="w-24 h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded text-right tabular-nums" />
                      <span className="text-[11px] text-[#6B7280]">{discount.kind === 'pct' ? '%' : 'UGX'}</span>
                    </div>
                  )}
                </div>
              </label>
            )}
          </div>

          {custMode !== 'walkin' && (
            <div className="border-t border-[#EEEDEA] pt-3">
              <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Deliver tickets via</div>
              <div className="flex gap-3">
                <label className="flex items-center gap-1.5 text-[12px] text-[#1F2937] cursor-pointer"><input type="checkbox" checked={deliverEmail} onChange={e => setDeliverEmail(e.target.checked)} />Email</label>
                <label className="flex items-center gap-1.5 text-[12px] text-[#1F2937] cursor-pointer"><input type="checkbox" checked={deliverWhatsapp} onChange={e => setDeliverWhatsapp(e.target.checked)} />WhatsApp</label>
                <label className="flex items-center gap-1.5 text-[12px] text-[#1F2937] cursor-pointer"><input type="checkbox" checked={deliverSms} onChange={e => setDeliverSms(e.target.checked)} />SMS</label>
              </div>
            </div>
          )}
        </div>
      )}

      <WizardFooter
        onBack={step !== 'customer' ? () => setStep(step === 'payment' ? 'tickets' : 'customer') : null}
        onCancel={onClose}
        onNext={() => {
          if (step === 'customer') { if (canContinueCustomer) setStep('tickets'); }
          else if (step === 'tickets') { if (canContinueTickets) setStep('payment'); }
          else { if (canSubmit) submit(); }
        }}
        nextLabel={submitting ? 'Creating…' : (step === 'payment' ? (comp ? 'Issue comp tickets' : 'Create order & issue tickets') : 'Continue')}
        nextDisabled={submitting || (step === 'customer' ? !canContinueCustomer : step === 'tickets' ? !canContinueTickets : !canSubmit)}
        primaryIcon={submitting ? Loader2 : (step === 'payment' ? Check : null)}
      />
      {pendingConfirm && (
        <OtpPromptModal
          open={true}
          onClose={() => { setPendingConfirm(null); }}
          action="dashboardconfirm"
          title="Authorize order confirmation"
          sub="A 6-digit code is required to finalize this order and issue tickets."
          onVerified={handleOtpVerified}
        />
      )}
    </Modal>
  );
};

/* ----------------------------------------------------------------------------
   EXPORT FLOW
   ---------------------------------------------------------------------------- */
